import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Sparkles, Loader2 } from "lucide-react";
import PhotoUploader from "../components/PhotoUploader";
import { toast } from "sonner";

const CATS = ["economy", "sedan", "suv", "luxury", "sports", "ev", "truck"];
const FUELS = ["gasoline", "hybrid", "electric", "diesel"];

export default function ListCar() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    make: "", model: "", year: 2024, trim: "", daily_price: 55,
    location: "", city: "", description: "",
    seats: 5, transmission: "automatic", fuel: "gasoline", category: "sedan",
    photos: [],
    features: "", mileage: 10000, gig_ready: true
  });
  const [aiBusy, setAiBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const suggest = async () => {
    if (!form.make || !form.model || !form.city) { toast.error("Fill make, model, city first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/pricing", {
        make: form.make, model: form.model, year: Number(form.year),
        category: form.category, city: form.city, mileage: Number(form.mileage) || null
      });
      setAi(data);
      upd("daily_price", data.suggested_daily_price);
      toast.success(`AI suggests $${data.suggested_daily_price}/day`);
    } catch (e) {
      toast.error("AI pricing unavailable");
    } finally { setAiBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.photos.length === 0) { toast.error("Add at least one photo"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year), daily_price: Number(form.daily_price),
        seats: Number(form.seats), mileage: Number(form.mileage) || null,
        features: form.features.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/cars", payload);
      toast.success("Listed!");
      nav(`/cars/${data.car_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to list");
    } finally { setBusy(false); }
  };

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-8 py-10" data-testid="list-car-page">
      <div className="mb-8 rise">
        <p className="font-mono text-xs tracking-widest uppercase text-cyan-400 mb-1">// List your car</p>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Earn with every mile.</h1>
      </div>
      <form onSubmit={submit} className="glass rounded-3xl p-8 space-y-5 rise rise-1">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Make</Label><Input required value={form.make} onChange={(e) => upd("make", e.target.value)} data-testid="form-make" className="bg-slate-900 border-slate-800 mt-1.5" /></div>
          <div><Label>Model</Label><Input required value={form.model} onChange={(e) => upd("model", e.target.value)} data-testid="form-model" className="bg-slate-900 border-slate-800 mt-1.5" /></div>
          <div><Label>Year</Label><Input type="number" required value={form.year} onChange={(e) => upd("year", e.target.value)} data-testid="form-year" className="bg-slate-900 border-slate-800 mt-1.5" /></div>
          <div><Label>Trim</Label><Input value={form.trim} onChange={(e) => upd("trim", e.target.value)} data-testid="form-trim" className="bg-slate-900 border-slate-800 mt-1.5" /></div>
          <div><Label>City</Label><Input required value={form.city} onChange={(e) => upd("city", e.target.value)} data-testid="form-city" className="bg-slate-900 border-slate-800 mt-1.5" /></div>
          <div><Label>Pickup location</Label><Input required value={form.location} onChange={(e) => upd("location", e.target.value)} data-testid="form-location" className="bg-slate-900 border-slate-800 mt-1.5" /></div>
          <div><Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => upd("category", v)}>
              <SelectTrigger data-testid="form-category" className="bg-slate-900 border-slate-800 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#131722] border-slate-800">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Fuel</Label>
            <Select value={form.fuel} onValueChange={(v) => upd("fuel", v)}>
              <SelectTrigger data-testid="form-fuel" className="bg-slate-900 border-slate-800 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#131722] border-slate-800">{FUELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Seats</Label><Input type="number" value={form.seats} onChange={(e) => upd("seats", e.target.value)} data-testid="form-seats" className="bg-slate-900 border-slate-800 mt-1.5" /></div>
          <div><Label>Mileage</Label><Input type="number" value={form.mileage} onChange={(e) => upd("mileage", e.target.value)} data-testid="form-mileage" className="bg-slate-900 border-slate-800 mt-1.5" /></div>
        </div>

        <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-cyan-300 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> AI-suggested daily price (USD)</Label>
              <Input type="number" required value={form.daily_price} onChange={(e) => upd("daily_price", e.target.value)} data-testid="form-daily-price" className="bg-slate-900 border-slate-800 mt-1.5" />
            </div>
            <Button type="button" onClick={suggest} disabled={aiBusy} data-testid="ai-suggest-btn" className="h-10 bg-cyan-500 text-slate-950 hover:bg-cyan-400 font-semibold">
              {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Suggest price <Sparkles className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
          {ai && <p className="text-xs text-slate-300 mt-2" data-testid="ai-reasoning">Range ${ai.range_low}-${ai.range_high} · {ai.confidence} confidence · {ai.reasoning}</p>}
        </div>

        <div><Label>Description</Label><Textarea required value={form.description} onChange={(e) => upd("description", e.target.value)} data-testid="form-description" className="bg-slate-900 border-slate-800 mt-1.5 min-h-[100px]" /></div>
        <div><Label>Photos</Label><div className="mt-1.5"><PhotoUploader photos={form.photos} onChange={(p) => upd("photos", p)} /></div></div>
        <div><Label>Features (comma-separated)</Label><Input value={form.features} onChange={(e) => upd("features", e.target.value)} data-testid="form-features" className="bg-slate-900 border-slate-800 mt-1.5" placeholder="CarPlay, Autopilot, Heated Seats" /></div>

        <div className="flex items-center justify-between p-4 rounded-xl bg-slate-900 border border-slate-800">
          <div>
            <Label className="text-base">Gig-ready</Label>
            <p className="text-xs text-slate-400">Suitable for Uber/Lyft/Delivery drivers.</p>
          </div>
          <Switch checked={form.gig_ready} onCheckedChange={(v) => upd("gig_ready", v)} data-testid="form-gig-ready" />
        </div>

        <Button type="submit" disabled={busy} data-testid="form-submit" className="w-full h-12 bg-white text-slate-950 hover:bg-slate-100 font-semibold btn-glow">
          {busy ? "Publishing…" : "Publish listing"}
        </Button>
      </form>
    </main>
  );
}
